/**
 * The train he is going for, kept across reloads.
 *
 * Stored beside the journey's own progress, under the same per-day rule, and thrown away with
 * the day. The format itself lives in core, where it can be tested.
 */

import { useCallback, useEffect, useState } from "react";
import { parsePin, serialisePin } from "../core/pinned-train";

function storageKey(journeyId: string): string {
	return `gotime.pin.${journeyId}`;
}

/** A pin belongs to the day it was made; yesterday's train is not a candidate for this morning. */
function today(): string {
	return new Date().toLocaleDateString("en-CA");
}

function read(journeyId: string): string | null {
	try {
		return parsePin(localStorage.getItem(storageKey(journeyId)), today());
	} catch {
		return null;
	}
}

export function usePinnedTrain(journeyId: string): [string | null, (next: string | null | ((current: string | null) => string | null)) => void] {
	const [pinnedId, setPinnedId] = useState<string | null>(() => read(journeyId));
	// Swapped during render rather than in an effect, so this journey's pin is never written
	// under the other journey's key first.
	const [loaded, setLoaded] = useState(journeyId);
	if (loaded !== journeyId) {
		setLoaded(journeyId);
		setPinnedId(read(journeyId));
	}

	useEffect(() => {
		try {
			localStorage.setItem(storageKey(journeyId), serialisePin(pinnedId, today()));
		} catch {
			// Private browsing; the pin simply will not survive a reload.
		}
	}, [journeyId, pinnedId]);

	const set = useCallback((next: string | null | ((current: string | null) => string | null)) => {
		setPinnedId((current) => (typeof next === "function" ? next(current) : next));
	}, []);

	return [pinnedId, set];
}
